import { FC, useCallback, useState } from 'react';
import { generateSequentialNumbers } from '../../../../services/generateSequentialNumbers';
import {
  CarAllocation,
  GetCarAllocationCondition,
} from '../../../../types/carAllocation';
import styles from './styles.module.css';
import Conditions from './Conditions';
import classNames from 'classnames';
import { useGetAllocationsInDriverOrderQuery } from '../../../../api/carAllocationApi';
import AllocationTableRow from './AllocationTableRow';
import { addDays, addMinutes, format, isSameDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import Popup from '../../../../components/common/Popup';
import AllocationDetails from './AllocationDetails';
import { getWorkCategoryIDByName } from '../../../../services/workCategory';
import { parseTime } from '../../../../services/parseTime';
import NoResultFound from '../../../../components/common/NoResultFound';
import PolicyRequired from '../../../../components/common/PolicyRequired';
import { FooterLayoutCenterize } from '../../../../components/FooterLayout';
import { useNavigate } from 'react-router-dom';

const isAllocationInTheDay = (allocation: CarAllocation, day: Date) => {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = addDays(dayStart, 1);
  const allocationWorkStart = parseTime(
    allocation.workStartTime,
    new Date(allocation.workStartDate)
  );
  const allocationWorkEnd = addMinutes(
    allocationWorkStart,
    allocation.taskEstimatedTime
  );

  const isWorkStartInTheDay =
    dayStart <= allocationWorkStart && allocationWorkStart < dayEnd;
  const isWorkEndInTheDay =
    dayStart <= allocationWorkEnd && allocationWorkEnd < dayEnd;

  return isWorkStartInTheDay || isWorkEndInTheDay;
};
type Props = {
  initConditions: GetCarAllocationCondition;
  conditions: GetCarAllocationCondition;
  onConditionChange: (conditions: GetCarAllocationCondition) => void;
  setLastCalculatedAt: (lastCalculatedAt: string) => void;
  isToastPopUp: boolean;
};
const WeekAllocations: FC<Props> = ({
  conditions,
  initConditions,
  onConditionChange,
  isToastPopUp,
}) => {
  const days = generateSequentialNumbers(0, 6).map((day) =>
    addDays(new Date(conditions.startDate), day)
  );
  const { data: allocations } = useGetAllocationsInDriverOrderQuery(conditions);
  const navigate = useNavigate();
  const lines = (allocations?.drivers || []).map((driver) => {
    return {
      title: driver.driverName,
      days: days.map((day) => {
        // 勤務状態が「出勤」かどうかチェックする
        const dayOff = !driver.workTimes
          .filter(({ date }) => isSameDay(day, new Date(date)))
          .every(({ workTime }) => workTime?.name === '出勤');
        if (dayOff)
          return {
            dayLabel: format(day, 'M/d(E)', { locale: ja }),
            dayOff: true as const,
          };

        const allocations = driver.cars.flatMap((car) => {
          return car.carAllocations.filter((a) => isAllocationInTheDay(a, day));
        });
        const taskAllocations = allocations.filter((a) =>
          (['回収', '処分', '保管', '積込'] as const)
            .map(getWorkCategoryIDByName)
            .includes(a.workCategory.id)
        );
        const workStartTime =
          allocations.find(
            (a) => a.workCategory.id === getWorkCategoryIDByName('出発')
          )?.workStartTime || '00:00';
        const workEndAllocation = allocations.find(
          (a) => a.workCategory.id === getWorkCategoryIDByName('到着')
        );
        let workEndTime: string;
        if (!workEndAllocation) {
          workEndTime = '23:59';
        } else {
          const workEndAllocationStartAt = parseTime(
            workEndAllocation.workStartTime || '24:00',
            new Date(workEndAllocation.workStartDate)
          );
          const workEnd = addMinutes(
            workEndAllocationStartAt,
            workEndAllocation.taskEstimatedTime
          );
          workEndTime = workEnd ? format(new Date(workEnd), 'H:mm') : '23:59';
        }

        return {
          dayLabel: format(day, 'M/d(E)', { locale: ja }),
          workStartTime,
          workEndTime,
          allocations: taskAllocations,
        };
      }),
    };
  });

  const noResult = allocations?.drivers.length === 0;

  const [popupState, setPopupState] = useState<PopupState>();
  const closePopup = useCallback(() => setPopupState(undefined), []);

  return (
    <>
      <PolicyRequired policy={34}>
        <Conditions
          conditions={conditions}
          initConditions={initConditions}
          onChange={onConditionChange}
        ></Conditions>
      </PolicyRequired>
      {!noResult ? (
        <div
          className={classNames(
            styles.timelines,
            isToastPopUp && styles.isToastPopUp
          )}
        >
          <div className={styles.timelineHeadRow}>
            <div
              className={classNames(
                styles.timelineHead,
                styles.timelineGroupKeyCell,
                styles.groupByCell
              )}
            >
              名前
            </div>
            {days.map((day) => (
              <div
                key={day.toISOString()}
                className={classNames(styles.timelineHead, styles.timeHeadCell)}
                onClick={() =>
                  navigate(
                    `/allocations?span=day&startDate=${format(
                      day,
                      'yyyy-MM-dd'
                    )}&endDate=${format(day, 'yyyy-MM-dd')}`
                  )
                }
              >
                {format(day, 'M/d(E)', { locale: ja })}
              </div>
            ))}
          </div>
          {lines.map((line) => (
            <AllocationTableRow
              line={line}
              key={line.title}
              onClickDay={(elem, dayLabel) => {
                // クリックした日の配車情報をポップアップとして開く
                const found = line.days.find((d) => d.dayLabel === dayLabel);
                if (!found?.allocations) return;
                setPopupState({
                  reference: elem,
                  allocations: found.allocations,
                });
              }}
            />
          ))}
        </div>
      ) : (
        <FooterLayoutCenterize>
          <div className={styles.noResultFound}>
            <NoResultFound />
          </div>
        </FooterLayoutCenterize>
      )}
      <PolicyRequired policy={30}>
        {popupState && (
          <Popup
            reference={popupState.reference}
            float
            onRequestClose={closePopup}
          >
            <AllocationDetails
              allocations={popupState.allocations}
              onRequestClose={closePopup}
            />
          </Popup>
        )}
      </PolicyRequired>
    </>
  );
};

export default WeekAllocations;

type PopupState =
  | {
      reference: HTMLElement;
      allocations: CarAllocation[];
    }
  | undefined;
